/**
 * useCampaignCanvas — client state for the split campaign canvas.
 *
 * Owns the derived campaign projection for a conversation:
 * - fetches GET /conversations/{id}/campaign-state (server is authoritative)
 * - applies `canvas` SSE patches optimistically mid-stream, with flash
 *   highlights on the changed fields
 * - posts inline edits (POST /canvas-edits) and approvals (POST /approvals),
 *   both of which append to message history so the projection replays exactly
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { apiClient } from '@/services/api';
import { jsonMergePatch, collectPatchPaths } from './mergePatch';
import type {
  CampaignState,
  DerivedCampaignState,
  Gate,
  Stage,
} from './types';

const FLASH_MS = 1400;

export interface CampaignCanvasApi {
  /** Latest derived projection (null until first fetch resolves). */
  derived: DerivedCampaignState | null;
  stage: Stage;
  state: CampaignState;
  nextGate: Gate | null;
  /** True once the conversation has any canvas content to show. */
  canvasActive: boolean;
  suggestions: string[];
  setSuggestions: (chips: string[]) => void;
  /** Re-fetch the authoritative projection from the server. */
  refresh: () => Promise<void>;
  /** Optimistically apply a patch from the `canvas` SSE event. */
  applyPatch: (patch: Record<string, unknown>) => void;
  /** Persist an inline canvas edit; resolves with the refreshed projection. */
  submitEdit: (path: string, value: unknown, label?: string) => Promise<DerivedCampaignState>;
  /** Approve the next stage gate; resolves with the refreshed projection. */
  approveGate: (gate: Gate, launchConfig?: Record<string, unknown>) => Promise<DerivedCampaignState>;
  /** Whether a field (dotted path) was just changed by a patch — drives flash. */
  isFlashing: (path: string) => boolean;
  approving: boolean;
  error: string | null;
  clearError: () => void;
}

export function useCampaignCanvas(conversationId?: string): CampaignCanvasApi {
  const [derived, setDerived] = useState<DerivedCampaignState | null>(null);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [approving, setApproving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [flashPaths, setFlashPaths] = useState<Set<string>>(new Set());
  const flashTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const refresh = useCallback(async () => {
    if (!conversationId) {
      setDerived(null);
      return;
    }
    try {
      const response = await apiClient.get<DerivedCampaignState>(
        `/api/v1/campaigns/${conversationId}/campaign-state`,
      );
      setDerived(response.data);
    } catch (e) {
      // Non-fatal: canvas simply stays hidden/stale; chat still works.
      console.warn('[useCampaignCanvas] failed to fetch campaign state', e);
    }
  }, [conversationId]);

  useEffect(() => {
    setDerived(null);
    setSuggestions([]);
    refresh();
  }, [refresh]);

  useEffect(() => () => {
    if (flashTimer.current) clearTimeout(flashTimer.current);
  }, []);

  const flash = useCallback((paths: string[]) => {
    if (paths.length === 0) return;
    setFlashPaths(new Set(paths));
    if (flashTimer.current) clearTimeout(flashTimer.current);
    flashTimer.current = setTimeout(() => setFlashPaths(new Set()), FLASH_MS);
  }, []);

  const applyPatch = useCallback(
    (patch: Record<string, unknown>) => {
      if (!patch || Object.keys(patch).length === 0) return;
      setDerived((prev) => {
        const base: DerivedCampaignState =
          prev ?? { stage: 'brief', state: {}, approvals: [], next_gate: 'strategy' };
        const nextState = jsonMergePatch(base.state, patch) as CampaignState;
        // The LLM cannot advance the stage, but a first patch does move the
        // canvas out of "brief" (content-driven transition, same as backend).
        const stage: Stage =
          base.stage === 'brief' && Object.keys(nextState).length > 0
            ? 'strategy'
            : base.stage;
        return { ...base, state: nextState, stage };
      });
      flash(collectPatchPaths(patch));
    },
    [flash],
  );

  const submitEdit = useCallback(
    async (path: string, value: unknown, label?: string) => {
      if (!conversationId) throw new Error('No conversation');
      try {
        const response = await apiClient.post<DerivedCampaignState>(
          `/api/v1/campaigns/${conversationId}/canvas-edits`,
          { path, value, label },
        );
        setDerived(response.data);
        flash([path.replace(/\[(\d+)\]/g, '.$1')]);
        return response.data;
      } catch (e: any) {
        const detail = e?.response?.data?.detail || 'Failed to save edit';
        setError(detail);
        throw e;
      }
    },
    [conversationId, flash],
  );

  const approveGate = useCallback(
    async (gate: Gate, launchConfig?: Record<string, unknown>) => {
      if (!conversationId) throw new Error('No conversation');
      setApproving(true);
      try {
        const response = await apiClient.post<DerivedCampaignState>(
          `/api/v1/campaigns/${conversationId}/approvals`,
          { gate, launch_config: launchConfig ?? null },
        );
        setDerived(response.data);
        return response.data;
      } catch (e: any) {
        const detail = e?.response?.data?.detail || `Failed to approve ${gate}`;
        setError(detail);
        throw e;
      } finally {
        setApproving(false);
      }
    },
    [conversationId],
  );

  const isFlashing = useCallback(
    (path: string) => {
      if (flashPaths.size === 0) return false;
      const normalized = path.replace(/\[(\d+)\]/g, '.$1');
      // A field flashes when the patch touched it or any ancestor/descendant.
      for (const p of flashPaths) {
        if (p === normalized || p.startsWith(`${normalized}.`) || normalized.startsWith(`${p}.`)) {
          return true;
        }
      }
      return false;
    },
    [flashPaths],
  );

  const stage: Stage = derived?.stage ?? 'brief';
  const state: CampaignState = derived?.state ?? {};
  const canvasActive =
    stage !== 'brief' || Object.keys(state).length > 0;

  return {
    derived,
    stage,
    state,
    nextGate: derived?.next_gate ?? 'strategy',
    canvasActive,
    suggestions,
    setSuggestions,
    refresh,
    applyPatch,
    submitEdit,
    approveGate,
    isFlashing,
    approving,
    error,
    clearError: () => setError(null),
  };
}
